import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { TurnstileService } from './turnstile.service';

/**
 * Forzado a `true` mientras estamos validando que la site key real (dev) esté bien
 * cargada y funcionando de punta a punta. Con 'interaction-only' Cloudflare puede
 * resolver en silencio si considera el navegador de bajo riesgo, y entonces el widget
 * no se ve nunca — útil en producción, pero no deja confirmar visualmente el QA.
 * Volver a `false` (o a la lógica automática de antes) una vez confirmado.
 */
const FORCE_VISIBLE_CHALLENGE = true;

/** Site keys de prueba documentadas por Cloudflare (1x/2x/3x + ceros): nunca son la key real de producción. */
const CLOUDFLARE_TEST_SITE_KEY_PATTERN = /^[123]x0000000000000000000/;

/**
 * Widget de Cloudflare Turnstile reutilizable.
 * Emite un token por `verified` cuando el usuario pasa el challenge; ese token
 * es el que el backend debe validar contra la API de siteverify de Cloudflare
 * antes de aceptar el formulario. El widget por sí solo no bloquea nada del
 * lado servidor, solo evita que bots completen el form sin pasar por acá.
 */
@Component({
  selector: 'app-turnstile',
  templateUrl: './turnstile.component.html',
  styleUrls: ['./turnstile.component.scss']
})
export class TurnstileComponent implements AfterViewInit, OnDestroy {
  @Input() siteKey!: string;
  @Input() theme: 'light' | 'dark' | 'auto' = 'light';
  /** Tiene que coincidir exacto (mayúsculas incluidas) con la action que valida el backend para este endpoint. */
  @Input() action?: string;
  /**
   * No ocupa espacio hasta llamar a execute(). Con la site key real usa 'interaction-only'
   * (invisible salvo que Cloudflare decida que hace falta interacción, que es el caso normal
   * para la mayoría de los usuarios). Con una site key de prueba de Cloudflare (1x/2x/3x) usa
   * 'execute' en cambio, porque esas keys fuerzan siempre el mismo resultado (pasa, bloquea, o
   * challenge interactivo) y con 'interaction-only' la de challenge forzado nunca se muestra,
   * dejando el submit colgado esperando un token que no llega.
   */
  @Input() invisibleUntilSubmit: boolean = false;
  @Output() verified = new EventEmitter<string>();
  @Output() expired = new EventEmitter<void>();
  @Output() errored = new EventEmitter<void>();

  @ViewChild('widgetContainer', { static: true }) widgetContainer!: ElementRef<HTMLDivElement>;

  private widgetId: string | undefined;
  private renderPromise: Promise<string> | undefined;

  constructor(private turnstileService: TurnstileService) {}

  ngAfterViewInit(): void {
    if (!this.siteKey || this.siteKey.includes('REPLACE_WITH')) {
      console.error('TurnstileComponent: falta configurar siteKey en setting.ts (turnstileSiteKey)');
      this.errored.emit();
      return;
    }

    this.renderPromise = this.turnstileService
      .render(this.widgetContainer.nativeElement, {
        sitekey: this.siteKey,
        theme: this.theme,
        ...(this.action ? { action: this.action } : {}),
        ...(this.invisibleUntilSubmit
          ? {
              execution: 'execute',
              appearance: (FORCE_VISIBLE_CHALLENGE || CLOUDFLARE_TEST_SITE_KEY_PATTERN.test(this.siteKey))
                ? 'execute'
                : 'interaction-only'
            } as const
          : {}),
        callback: (token: string) => this.verified.emit(token),
        'expired-callback': () => this.expired.emit(),
        'error-callback': () => this.errored.emit()
      });

    this.renderPromise
      .then((widgetId) => {
        this.widgetId = widgetId;
      })
      .catch((error) => {
        console.error('Error inicializando Cloudflare Turnstile:', error);
        this.errored.emit();
      });
  }

  /** Dispara la validación en modo `execution: 'execute'`. Llamar al enviar el formulario. */
  async execute(): Promise<void> {
    const widgetId = this.widgetId ?? (await this.renderPromise?.catch(() => undefined));
    if (widgetId) {
      this.turnstileService.execute(widgetId);
    } else {
      this.errored.emit();
    }
  }

  /** Limpia el challenge resuelto y fuerza uno nuevo. Llamar tras un submit exitoso o fallido. */
  reset(): void {
    this.turnstileService.reset(this.widgetId);
  }

  ngOnDestroy(): void {
    this.turnstileService.remove(this.widgetId);
  }
}
